import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import MaldivesWasPriceRow from './MaldivesWasPriceRow'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import { isRewardFareClass, type FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { useAppSelector } from 'hooks/reduxHooks'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
// Hooks
import { useCalendarItemData } from '../hooks/useCalendarItemData'
import { useCharterRewardAvailabilityContext } from '../hooks/charterRewardAvailabilityContext'
import type { CalendarItemBundleData } from '../hooks/types'
import useCalendarItemPricing from './useCalendarItemPricing'
import { computeFareClassCellPrice } from './maldivesCharterTripOptionsTablePricing'
import { useSeatEligiblePassengers } from '../hooks/utils'
import MaldivesFnfSpecialOffer from './MaldivesFnfSpecialOffer'
import MaldivesCharterRewardSeatsAvailableLabel from './MaldivesCharterRewardSeatsAvailableLabel'
import MaldivesCharterDateFareCarousel from './MaldivesCharterDateFareCarousel'
import MaldivesCharterDateRowFareToggle from './MaldivesCharterDateRowFareToggle'
import type { MaldivesCharterTripOption } from './MaldivesCharterFareCarouselOption'

// Utils
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'

// Types
interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  fareClass?: FareClassWithReward
}

interface Props {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  selectedFareClass?: FareClassWithReward | null
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  isSelected: boolean
  onSelect: (option: TripOption) => void
  prefetchedBundleData?: CalendarItemBundleData
  /** Available fare classes (including reward variants) for the per-date fare carousel. */
  allowedFareClasses?: ReadonlyArray<FareClassWithReward>
  /** Callback when the user picks a different fare class from the carousel. */
  onFareClassChange?: (fareClass: FareClassWithReward) => void
}

// Styled Components - Mobile specific layout
const DateCard = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const SelectButton = styled(TextButton)`
  min-width: ${rem(80)};
`

const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
`

const SmallSup = styled.sup`
  font-size: 0.6em;
  vertical-align: super;
`

function MaldivesDirectSearchFlightCalendarItemMobile(props: Props) {
  const {
    departDate,
    returnDate,
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    selectedFareClass,
    priceMode,
    luxPlusEnabled,
    isSelected,
    onSelect,
    prefetchedBundleData,
    allowedFareClasses,
    onFareClassChange,
  } = props

  const intl = useIntl()
  const [fareCarouselExpanded, setFareCarouselExpanded] = useState(false)
  const dateFormatter = useIntlDateFormatter()
  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)
  const { isKidsFlyFreeDate } = useKidsFlyFree({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms,
    fareClass,
  })

  const fetchedBundleData = useCalendarItemData({
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    priceMode,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    disabled: !!prefetchedBundleData,
  })

  const bundleData = prefetchedBundleData ?? fetchedBundleData

  const { isAvailableForDate: isRewardAvailableForDate, getLegResult, getJourneysForCabinDate } = useCharterRewardAvailabilityContext()
  const showRewardSeatsLabel = isRewardAvailableForDate(checkIn)

  // Reward pricing requires authentication to query svc-lux-loyalty
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const isRewardSelected = !!selectedFareClass && isRewardFareClass(selectedFareClass)
  const rewardLockedForLogin = isRewardSelected && !memberId
  const showModal = useContext(ModalContext)
  const handleLoginClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])
  const rewardCellPrice = useMemo(() => {
    if (!isRewardSelected || !selectedFareClass) return undefined
    const cabinJourneys = getJourneysForCabinDate(toBaseFareClass(selectedFareClass), checkIn)
    return computeFareClassCellPrice({
      fareClass: selectedFareClass,
      hotelPrice: bundleData.hotelPrice,
      hotelMemberPrice: bundleData.hotelMemberPrice,
      departingFlight: cabinJourneys?.departing,
      returningFlight: cabinJourneys?.returning,
      seatEligiblePassengers,
      getLegResult,
    })
  }, [isRewardSelected, selectedFareClass, getJourneysForCabinDate, checkIn, bundleData.hotelPrice, bundleData.hotelMemberPrice, seatEligiblePassengers, getLegResult])

  const {
    regularPrice,
    memberPrice,
    isMemberPrice,
    displayPrice,
    wasPrice,
    shouldShowWasPrice,
    inventoryMessageType,
    inventoryMessage,
    fnfInventoryMessage,
    specialOfferLabel,
  } = useCalendarItemPricing({
    bundleData,
    luxPlusEnabled,
    priceMode,
  })

  const option: TripOption = {
    id: `${checkIn}-${nights}n`,
    departDate: departDate,
    returnDate: returnDate,
    checkIn: checkIn,
    checkOut: checkOut,
    nights,
    price: regularPrice,
    memberPrice: memberPrice,
    currency: 'AUD',
    seatsLeft: bundleData.seatsLeft,
    available: Boolean(bundleData.available),
    fareClass: selectedFareClass ?? undefined,
  }

  const selectedText = intl.formatMessage({
    id: 'acuxSK1zMav7-Sn9mh4K',
    defaultMessage: 'Selected',
    description: 'Button label when a charter flight date is currently selected',
  })
  const unavailableText = intl.formatMessage({
    id: 'MkdIPUtrVEQzhLPF4i4b',
    defaultMessage: 'Unavailable',
    description: 'Button label when charter flight date is unavailable',
  })
  const loadingText = intl.formatMessage({
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  })
  const nightsAndFlightsText = intl.formatMessage(
    {
      id: 'r484bkJ_LnqWqW8S-s9D',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights',
      description: 'Label showing number of nights and flights included in charter bundle',
    },
    { nights },
  )
  const dayOffsetText = intl.formatMessage({
    id: 'l9QfkSZq5TknjYVpdoEi',
    defaultMessage: '+1',
    description: 'Day offset indicator showing arrival is next day for charter flight',
  })
  const changeFareLabel = intl.formatMessage({
    id: 'Rt6uQBd5FYfbS4hAV4ab',
    defaultMessage: 'Change fare',
    description: 'Link to reopen the fare carousel for an already-selected Maldives charter date',
  })
  const expandAriaLabel = intl.formatMessage({
    id: '711wxG49umcK97pt8pS-',
    defaultMessage: 'Show fare options for these dates',
    description: 'Accessibility label for expanding the fare carousel beneath the row.',
  })
  const collapseAriaLabel = intl.formatMessage({
    id: '32x_r3aTH5F8f-0HHeLj',
    defaultMessage: 'Hide fare options for these dates',
    description: 'Accessibility label for collapsing the fare carousel shown below the row.',
  })
  const unavailableAriaLabel = intl.formatMessage({
    id: 'OwJEwq2JWiXn3Mtk7a4k',
    defaultMessage: 'Fare options unavailable for these dates',
    description: 'Accessibility label for the disabled fare-toggle control.',
  })

  // Render price display
  let priceDisplay
  if (bundleData.fetching) {
    priceDisplay = (
      <BodyText variant="small" colour="light-foreground-alt">
        {loadingText}
      </BodyText>
    )
  } else if (rewardLockedForLogin) {
    priceDisplay = (
      <VerticalSpacer gap={8}>
        <BodyText variant="medium" colour="neutral-two">
          {intl.formatMessage({
            id: 'kx9HisAhmWTyXzvb0xem',
            defaultMessage: 'Log in to view',
            description: 'Label shown when a logged-out user has a reward fare class selected; they must log in to see reward pricing.',
          })}
        </BodyText>
        <TextButton kind="secondary" size="small" onClick={handleLoginClick}>
          {intl.formatMessage({
            id: 'kx9HisAhmWTyXzvb0xem',
            defaultMessage: 'Log in to view',
            description: 'Label shown when a logged-out user has a reward fare class selected; they must log in to see reward pricing.',
          })}
        </TextButton>
      </VerticalSpacer>
    )
  } else if (isRewardSelected && rewardCellPrice?.pointsTotal !== undefined && rewardCellPrice.cashTotal !== undefined) {
    const showMemberReward = luxPlusEnabled && rewardCellPrice.cashMemberTotal != null
    const rewardCashDisplay = showMemberReward ? rewardCellPrice.cashMemberTotal! : rewardCellPrice.cashTotal
    const rewardColour = showMemberReward ? 'lux-plus' as const : undefined
    const totalLabel = intl.formatMessage({
      id: 'j--PuUYLTgWUxAxROh0Z',
      defaultMessage: 'total',
      description: 'Total pricing unit suffix in the Maldives calendar',
    })
    priceDisplay = (
      <>
        <PriceRowPriceCaption>{intl.formatMessage({
          id: '61Gm0owvNXkEHBOdcx8J',
          defaultMessage: 'From',
          description: 'Price prefix label in Maldives flight calendar item',
        })}
        </PriceRowPriceCaption>
        <Heading as="div" variant="heading4" colour={rewardColour} wrap="no-wrap">
          <FormatLoyaltyPoints abbreviated points={rewardCellPrice.pointsTotal} />
        </Heading>
        <Caption variant="medium" colour={rewardColour} weight="normal">
          <FormattedMessage
            id="peHFjMVc2YKKQdYS2mU5"
            defaultMessage="+ {cash}"
            description="Cash co-pay portion of a Société reward-seat bundle in the Maldives charter calendar, shown under the points total in a smaller line with a leading plus sign before the formatted currency amount."
            values={{ cash: <FormatCurrency value={rewardCashDisplay} format="roundedDollar" /> }}
          />
        </Caption>
        <Caption variant="large" colour={rewardColour} weight="normal" format="lowercase">
          {totalLabel}
        </Caption>
      </>
    )
  } else if (isRewardSelected) {
    // Reward class selected but no reward seats available for this date
    priceDisplay = (
      <BodyText variant="medium" colour="neutral-three">
        {intl.formatMessage({
          id: 'hsy10eR5PC7a80SwwpJ6',
          defaultMessage: 'No seats available',
          description: 'Short label when a fare column has no reward-seat inventory or no seats for that cabin',
        })}
      </BodyText>
    )
  } else if (displayPrice > 0) {
    const perPersonLabel = intl.formatMessage({
      id: 'ew2ARy7ro3tFSM4inpHN',
      defaultMessage: '/person',
      description: 'Per person pricing unit suffix in Maldives calendar',
    })
    const totalLabel = intl.formatMessage({
      id: 'j--PuUYLTgWUxAxROh0Z',
      defaultMessage: 'total',
      description: 'Total pricing unit suffix in the Maldives calendar',
    })
    priceDisplay = (
      <>
        <PriceRowPriceCaption>{intl.formatMessage({
          id: '61Gm0owvNXkEHBOdcx8J',
          defaultMessage: 'From',
          description: 'Price prefix label in Maldives flight calendar item',
        })}
        </PriceRowPriceCaption>
        <div>
          <FormattedMessage
            id="eEOhCaC58xEuJ7A6ucxl"
            defaultMessage="{price} {unit}"
            description="Price amount followed by pricing unit (per person or total) in Maldives flight calendar mobile"
            values={{
              price: <Heading as="span" variant="heading4" colour={isMemberPrice ? 'lux-plus' : undefined} wrap="no-wrap">
                <FormatCurrency value={displayPrice} format="roundedDollar" />
              </Heading>,
              unit: <Caption as="span" variant="large" colour={isMemberPrice ? 'lux-plus' : 'light-foreground-alt'} weight="normal" format="lowercase">
                {priceMode === 'perPerson' ? perPersonLabel : totalLabel}
              </Caption>,
            }}
          />
        </div>
        {shouldShowWasPrice && wasPrice != null && <MaldivesWasPriceRow
          altPrice={wasPrice}
          saleUnit={priceMode === 'perPerson' ? '/person' : undefined}
        />}
      </>
    )
  } else {
    priceDisplay = (
      <BodyText variant="medium" colour="light-foreground-alt">
        {unavailableText}
      </BodyText>
    )
  }

  useEffect(() => {
    if (isSelected) {
      setFareCarouselExpanded(true)
    }
  }, [isSelected])

  const selectControl = useMemo(() => {
    if (isSelected && !fareCarouselExpanded) {
      return (
        <Group direction="vertical" gap={4} horizontalAlign="end">
          <SelectButton
            kind="primary"
            nonInteractable
            tabIndex={-1}
          >
            {selectedText}
          </SelectButton>
          <TextLink size="caption" weight="regular" onClick={() => setFareCarouselExpanded(true)}>
            {changeFareLabel}
          </TextLink>
        </Group>
      )
    }
    return (
      <MaldivesCharterDateRowFareToggle
        expanded={fareCarouselExpanded}
        disabled={!bundleData.available}
        onExpand={() => setFareCarouselExpanded(true)}
        onCollapse={() => setFareCarouselExpanded(false)}
        expandAriaLabel={expandAriaLabel}
        collapseAriaLabel={collapseAriaLabel}
        unavailableAriaLabel={unavailableAriaLabel}
      />
    )
  }, [isSelected, fareCarouselExpanded, bundleData.available, selectedText, changeFareLabel, expandAriaLabel, collapseAriaLabel, unavailableAriaLabel])

  const carouselTripOption: MaldivesCharterTripOption = useMemo(() => ({
    id: option.id,
    departDate: option.departDate,
    returnDate: option.returnDate,
    checkIn: option.checkIn,
    checkOut: option.checkOut,
    nights: option.nights,
    price: option.price,
    memberPrice: option.memberPrice,
    currency: option.currency,
    seatsLeft: option.seatsLeft,
    available: option.available,
  }), [option.id, option.departDate, option.returnDate, option.checkIn, option.checkOut, option.nights, option.price, option.memberPrice, option.currency, option.seatsLeft, option.available])

  const handleCarouselPickFare = useCallback((pickedFareClass: FareClassWithReward, pickedOption: MaldivesCharterTripOption) => {
    onFareClassChange?.(pickedFareClass)
    onSelect({
      ...pickedOption,
      fareClass: pickedFareClass,
    })
  }, [onSelect, onFareClassChange])

  return (
    <DateCard>
      <VerticalSpacer gap={12}>
        {/* Dates Row */}
        <DateRangeContainer>
          <div>
            <BodyText variant="small" colour="light-foreground-alt">
              {dateFormatter(departDate, INTL_LONG_WEEKDAY)}
            </BodyText>
            <Heading variant="heading5" wrap="no-wrap">
              {dateFormatter(departDate, INTL_DMY_CASUAL_FORMAT)}
            </Heading>
          </div>
          <Group direction="vertical" gap={0} horizontalAlign="center">
            <LineArrowRightIcon size="M" />
            <BodyText variant="small" colour="light-foreground-alt">
              {nightsAndFlightsText}
            </BodyText>
          </Group>
          <Group direction="vertical" horizontalAlign="end">
            <Group direction="vertical" horizontalAlign="start" gap={0}>
              <BodyText variant="small" colour="light-foreground-alt">
                {dateFormatter(checkOut, INTL_LONG_WEEKDAY)}
              </BodyText>
              <Heading variant="heading5" wrap="no-wrap">
                {dateFormatter(checkOut, INTL_DMY_CASUAL_FORMAT)}<SmallSup>{dayOffsetText}</SmallSup>
              </Heading>
            </Group>
          </Group>
        </DateRangeContainer>

        {/* Kids Fly Free + Availability Warning */}
        {(() => {
          return (
            <Group direction="vertical" horizontalAlign="start" gap={0}>
              {(!bundleData.isFnfPriceApplied || inventoryMessageType !== 'seat') && inventoryMessage !== '' && <Group direction="horizontal" verticalAlign="center" gap={4}>
                <SolidExclamationCircleIcon colour="urgency" size="XS" />
                <BodyText variant="small" colour="critical">
                  {inventoryMessage}
                </BodyText>
              </Group>}
              {bundleData.isFnfPriceApplied && (
                <MaldivesFnfSpecialOffer
                  specialOfferLabel={specialOfferLabel}
                  fnfInventoryMessage={fnfInventoryMessage}
                  isHorizontal
                />
              )}
              {isKidsFlyFreeDate(departDate) && <KidsFlyFreeLabel variant="short" />}
              {showRewardSeatsLabel && <MaldivesCharterRewardSeatsAvailableLabel />}
            </Group>
          )
        })()}

        {/* Price and Select Button */}
        <Group direction="horizontal" horizontalAlign={fareCarouselExpanded ? 'end' : 'space-between'} verticalAlign="center" gap={12}>
          {!fareCarouselExpanded && (
            <VerticalSpacer gap={4}>
              {priceDisplay}
            </VerticalSpacer>
          )}
          {selectControl}
        </Group>
      </VerticalSpacer>
      {fareCarouselExpanded && allowedFareClasses && allowedFareClasses.length > 1 && (
        <MaldivesCharterDateFareCarousel
          fareClasses={allowedFareClasses}
          selectedFareClass={selectedFareClass ?? fareClass}
          isDateRowSelected={isSelected}
          checkIn={checkIn}
          checkOut={checkOut}
          nights={nights}
          rooms={rooms}
          priceMode={priceMode}
          luxPlusEnabled={luxPlusEnabled}
          tripOption={carouselTripOption}
          onPickFare={handleCarouselPickFare}
        />
      )}
    </DateCard>
  )
}

export default MaldivesDirectSearchFlightCalendarItemMobile
